import dayjs from "dayjs";
import { FileQuestion, LucideCircleQuestionMark } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { matchPath, useNavigate } from "react-router-dom";

import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button.js";
import { Modal } from "@/app/components/ui/modal.jsx";
import { stripHTML } from "@/app/handlers/validationFunctions.js";
import { useFeatureFlag } from "@/app/hooks/useFeatureFlag.jsx";
import { useLatestJobs } from "@/app/services/create-resume/queries.js";
import FitCheck, { JobDetails as FitCheckJobDetails } from "@/app/Views/FitCheck/FitCheck";

import useIsLoggedIn from "../../../../../hooks/use-IsLoggedIn";
import DSPModal from "../../../../JobsPage/components/jobApplication/DSPModal";
import GenerateSingleJobImage from "../../../../Social/GenerateSingleJobImage";
import ApplyModal from "../ApplyModal";
import Loader from "../Loader";
// Components
import { ApplicationProcess } from "./components/ApplicationProcess";
import { CompanyCard } from "./components/CompanyCard";
import { FitCheckUpsell } from "./components/FitCheckUpsell";
import { JobActionButtons } from "./components/JobActionButtons";
import { JobDescription } from "./components/JobDescription";
import { JobHeader } from "./components/JobHeader";
import { JobMetadata } from "./components/JobMetadata";
import { MoreJobs } from "./components/MoreJobs";
// Hooks
import { useFitCheckFlow } from "./hooks/useFitCheckFlow";
import { useJobApplication } from "./hooks/useJobApplication";
import { useJobDetails } from "./hooks/useJobDetails";
import { useJobInteractions } from "./hooks/useJobInteractions";
import { useRoleExplainer } from "./hooks/useRoleExplainer";
// Utils
import { isJobExpired, JobDetails, toLowerCaseCapitalize } from "./utils/helpers";

interface ViewJobProps {
	data?: { _id?: string };
	setData?: (data: JobDetails) => void;
	setShowJobDetailsModal?: (show: boolean) => void;
}

function ViewJob({ data, setData, setShowJobDetailsModal }: ViewJobProps) {
	const baseUrl = `${window.location.origin}/`;
	const navigate = useNavigate();
	const isUserLoggedIn = useIsLoggedIn();
	const isOnJobPage = window.location.pathname.includes("/jobs/");

	// embedPage is true when we are on /companies/:id/embed page
	const embedPage = matchPath("/companies/:id/embed", window.location.pathname);

	// Custom hooks
	const { jobId, jobDetails, loading, applicationDetails, setApplicationDetails } = useJobDetails(data);
	const { submitting, saveJobInteraction } = useJobInteractions(jobId, data?._id);
	const { roleExplanations, explainerIsLoading, handleRoleExplainer } = useRoleExplainer(jobDetails);
	const { showFitCheckMe, setShowFitCheckMe, handleFitCheckMe, isOnProfilePage, isInBasket } = useFitCheckFlow(
		jobDetails,
		setShowJobDetailsModal,
	);
	const {
		showDSPModal,
		setShowDSPModal,
		showApplyModal,
		setShowApplyModal,
		scrollToEmail,
		applicationRef,
		onApply,
		handleDSPModalSelect,
		dspModalData,
	} = useJobApplication(applicationDetails, setApplicationDetails, isUserLoggedIn, jobId);

	// More jobs
	const [moreJobs, setMoreJobs] = useState<JobDetails[]>([]);
	const { data: response, isLoading } = useLatestJobs();

	useEffect(() => {
		if (!isLoading && response) {
			setMoreJobs(response.data);
		}
	}, [response, isLoading]);

	// Feature flags
	const showPostGenerator = useFeatureFlag("posterGenerator");

	// Post generator modal
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleMenuClick = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);

	// Navigation
	const handleBackButton = () => {
		if (isOnJobPage) {
			navigate("/jobs");
		} else if (setShowJobDetailsModal) {
			setShowJobDetailsModal(false);
		}
	};

	// Job selection handlers for MoreJobs
	const handleJobSelect = (job: JobDetails) => {
		window.scrollTo(0, 0);
		if (setData && data) {
			setData(job);
		} else {
			navigate(`/jobs/${job?._id}`);
		}
	};

	const handleJobClick = (job: JobDetails) => {
		window.scrollTo(0, 0);
		if (setData && data) {
			setData(job);
			setShowJobDetailsModal?.(true);
		} else {
			navigate(`/jobs/${job?._id}`);
		}
	};

	return (
		<>
			{loading && (
				<div className="flex items-center justify-center min-h-screen">
					<Loader />
				</div>
			)}
			{!loading && (
				<>
					<div className="min-h-screen">
						<Helmet>
							<title>
								{jobDetails?.jobTitle} job at {jobDetails?.company?.company_name || ""} in{" "}
								{jobDetails?.jobLocation?.region}, Tanzania
							</title>
							<meta name="description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta
								name="keywords"
								content={`${jobDetails?.jobTitle} job, ${jobDetails?.company?.company_name || ""}, ${jobDetails?.jobLocation?.region} Tanzania`}
							/>
							<meta property="og:title" content={jobDetails?.jobTitle} />
							<meta property="og:description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta property="og:image" content={jobDetails?.company?.logo_url} />
							<meta property="og:url" content={window.location.href} />
						</Helmet>

						<div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-8">
							<JobHeader
								jobDetails={jobDetails}
								baseUrl={baseUrl}
								onBack={handleBackButton}
								onShare={() => saveJobInteraction("Shared")}
							/>

							<div className="mb-8">
								<div>
									<JobMetadata
										jobDetails={jobDetails}
										showPostGenerator={showPostGenerator}
										onMenuClick={handleMenuClick}
									/>

									{/* Action buttons */}
									{!isJobExpired(jobDetails.closeAt) && !showFitCheckMe && (
										<JobActionButtons
											jobDetails={jobDetails}
											submitting={submitting}
											isUserLoggedIn={isUserLoggedIn}
											isOnProfilePage={isOnProfilePage}
											applicationDetails={applicationDetails}
											explainerIsLoading={explainerIsLoading}
											isInBasket={isInBasket}
											onSave={() => saveJobInteraction("Saved")}
											onApply={onApply}
											onFitCheckMe={handleFitCheckMe}
											onRoleExplainer={handleRoleExplainer}
											onNotRelevant={() => saveJobInteraction("Irrevant")}
										/>
									)}
								</div>

								{/* FitCheck toggle */}
								{showFitCheckMe && (
									<div className="pt-5" style={{ borderTopWidth: "1px" }}>
										<div className="flex gap-4 mb-8">
											<Button variant="secondary" onClick={() => setShowFitCheckMe(false)}>
												Job Details
											</Button>
											<Button variant="tonal">FitCheck</Button>
										</div>
									</div>
								)}

								{/* Main content: FitCheck or Job Details */}
								{showFitCheckMe ? (
									<FitCheck jobDetails={jobDetails as unknown as FitCheckJobDetails} />
								) : (
									<div className="pt-3" style={{ borderTopWidth: roleExplanations ? undefined : "1px" }}>
										{/* Role Explainer CTA */}
										<div className="flex items-center justify-end">
											<Button onClick={handleRoleExplainer} size="sm" variant="link" disabled={explainerIsLoading}>
												{explainerIsLoading ? (
													<div className="flex items-center space-x-2">
														<span className="w-4 h-4">
															<Spinner />
														</span>
														<span className="text-sm">Explaining...</span>
													</div>
												) : (
													<span className="flex gap-2 items-center">
														<LucideCircleQuestionMark className="h-4 w-4" />
														Explain Role
													</span>
												)}
											</Button>
										</div>

										<JobDescription jobDetails={jobDetails} roleExplanations={roleExplanations} />

										<ApplicationProcess
											jobDetails={jobDetails}
											applicationDetails={applicationDetails}
											scrollToEmail={scrollToEmail}
											applicationRef={applicationRef}
											onApply={onApply}
										/>

										{/* FitCheckMe Upsell Section */}
										{!embedPage && <FitCheckUpsell onFitCheckClick={handleFitCheckMe} />}
									</div>
								)}
							</div>

							{/* Company Card */}
							{!embedPage && <CompanyCard jobDetails={jobDetails} />}

							{/* More Jobs */}
							{!embedPage && (
								<MoreJobs
									moreJobs={moreJobs}
									currentJobId={jobDetails?._id}
									onJobSelect={handleJobSelect}
									onJobClick={handleJobClick}
								/>
							)}

							{/* Apply Modal */}
							<ApplyModal
								isOpen={showApplyModal}
								onClose={() => setShowApplyModal(false)}
								jobTitle={jobDetails.jobTitle}
								companyName={(jobDetails as { companyName?: string }).companyName}
								applicationDetails={applicationDetails}
								onDrawer={true}
							/>

							<DSPModal
								data={dspModalData}
								isOpen={showDSPModal}
								onSubmit={handleDSPModalSelect}
								onClose={() => setShowDSPModal(false)}
								action={(dspModalData as { action?: string })?.action}
							/>
						</div>
					</div>

					{/* Post Generator Modal */}
					<Modal
						isOpen={isModalOpen}
						onClose={handleCloseModal}
						title="Generate Image"
						description=""
						className=""
						titleClassName=""
					>
						<GenerateSingleJobImage
							companyName={jobDetails?.company?.company_name || ""}
							jobTitle={jobDetails?.jobTitle}
							jobDescription={stripHTML(jobDetails?.jobDescription?.lookingToHire).split(".")[0]}
							qualification={
								toLowerCaseCapitalize(jobDetails?.educationQualification?.minEducationLevel) +
									" in " +
									(jobDetails?.educationQualification?.relevantMajors?.join(", ") || "") || ""
							}
							deadline={dayjs(jobDetails?.closeAt).format("MMM DD, YYYY")}
							jobLocation={`${jobDetails?.jobLocation?.region}`}
							jobLink={`www.dproz.com/companies/${jobDetails?.company?._id}`}
						/>
					</Modal>
				</>
			)}
		</>
	);
}

export default ViewJob;
